// SVG-based zone overlay used by the operator, display and public live views.
// (The layout EDITOR uses react-konva; this read/tap overlay is plain SVG so the
// public bundle stays free of Konva.)
//
// Renders the venue image with all zones laid over it at the same fractional
// positions, regardless of the rendered image size. Tap handlers are passed in
// optionally — the public live view passes none, which is the read-only invariant.

import { useRef } from "react";
import { useElementSize } from "../lib/useElementSize";
import { fitImageInContainer, zoneRectPx, zoneCirclePx, zoneQuadPx } from "../lib/geometry";
import type { WithId, ZoneDoc } from "../types";

// Local "claiming" state lives in the operator UI as a Set<zoneId> — the truth
// comes from Firestore via `zones`. We pass it in so the shape can render
// optimistically the instant a tap is registered.
export interface ZoneOverlayProps {
  venueImageUrl: string | null;
  imageAspectRatio: number | null;
  zones: WithId<ZoneDoc>[];
  claiming?: Set<string>;
  // Optional agent colour tint for booked zones (display only)
  agentColourMap?: Record<string, string>;
  onZoneTap?: (zone: WithId<ZoneDoc>) => void;
  // When true, all pointer events on zones are suppressed (public live view).
  readOnly?: boolean;
  // Add a state icon (tick/dot) so colour isn't the only signal — used by the
  // public live view per the spec ("don't rely on colour alone").
  showStateIcons?: boolean;
}

// Every fill/stroke alpha is multiplied by the event-controlled
// --z-fill-opacity CSS variable (defaults to 1, set by applyBranding when
// EventBranding.zoneOpacity is configured). At opacity 0 every zone fill
// and stroke disappears, leaving only the white label number visible.
const STATE_FILL: Record<string, string> = {
  available: "rgb(var(--z-available) / calc(0.55 * var(--z-fill-opacity, 1)))",
  pending:   "rgb(var(--z-pending)   / calc(0.75 * var(--z-fill-opacity, 1)))",
  booked:    "rgb(var(--z-booked)    / calc(0.85 * var(--z-fill-opacity, 1)))",
  claiming:  "rgb(var(--z-claiming)  / calc(0.85 * var(--z-fill-opacity, 1)))",
};

const STATE_STROKE: Record<string, string> = {
  available: "rgb(var(--z-available) / var(--z-fill-opacity, 1))",
  pending:   "rgb(var(--z-pending)   / var(--z-fill-opacity, 1))",
  booked:    "rgb(var(--z-booked)    / var(--z-fill-opacity, 1))",
  claiming:  "rgb(var(--z-claiming)  / var(--z-fill-opacity, 1))",
};

export function ZoneOverlay(props: ZoneOverlayProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const { w: containerW, h: containerH } = useElementSize(containerRef);
  const ar = props.imageAspectRatio ?? 16 / 9;
  const fit = fitImageInContainer(
    containerW || 1,
    containerH || 1,
    ar
  );

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full select-none"
      style={props.readOnly ? { pointerEvents: "none" } : undefined}
    >
      {/* Image */}
      {props.venueImageUrl ? (
        <img
          src={props.venueImageUrl}
          alt=""
          className="absolute pointer-events-none object-contain"
          style={{
            left: fit.offsetX,
            top: fit.offsetY,
            width: fit.imgW,
            height: fit.imgH,
          }}
          draggable={false}
        />
      ) : (
        <div
          className="absolute bg-bg-elev border border-border rounded-md flex flex-col items-center justify-center gap-2 text-fg-muted"
          style={{
            left: fit.offsetX,
            top: fit.offsetY,
            width: fit.imgW,
            height: fit.imgH,
          }}
        >
          {/* Inline SVG (not lucide-react) — this file ships in the eager
              /live bundle and must stay icon-library-free. */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-10 h-10 opacity-40"
            aria-hidden
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <span className="text-sm">No venue image</span>
        </div>
      )}

      {/* Zone overlay — same coordinate space as the image */}
      <svg
        className="absolute"
        style={{
          left: fit.offsetX,
          top: fit.offsetY,
          width: fit.imgW,
          height: fit.imgH,
          overflow: "visible",
        }}
        viewBox={`0 0 ${Math.max(fit.imgW, 1)} ${Math.max(fit.imgH, 1)}`}
        preserveAspectRatio="none"
      >
        {props.zones.map((z) => {
          const isClaiming = props.claiming?.has(z.id);
          const renderedState = isClaiming ? "claiming" : z.status;
          const fill = STATE_FILL[renderedState];
          const stroke = STATE_STROKE[renderedState];
          const bookedAgentTint =
            z.status === "booked" && z.pendingBy && props.agentColourMap?.[z.pendingBy]
              ? props.agentColourMap[z.pendingBy]
              : null;

          const onClick =
            props.readOnly || !props.onZoneTap
              ? undefined
              : (e: React.MouseEvent | React.PointerEvent) => {
                  e.stopPropagation();
                  props.onZoneTap?.(z);
                };

          const pulseClass = renderedState === "pending" ? "animate-pulse-pending" : "";
          const cursorClass = props.readOnly ? "" : z.status === "available" ? "cursor-pointer" : "";

          if (z.shape === "circle") {
            const c = zoneCirclePx(z, fit.imgW, fit.imgH);
            const boxSize = c.r * 2;
            return (
              <g key={z.id} className={`${pulseClass} ${cursorClass}`} onClick={onClick}>
                <circle
                  cx={c.cx}
                  cy={c.cy}
                  r={c.r}
                  fill={bookedAgentTint ?? fill}
                  stroke={stroke}
                  strokeWidth={2}
                />
                <ZoneLabel x={c.cx} y={c.cy} label={z.label} boxSize={boxSize} />
                {props.showStateIcons && (
                  <StateIcon x={c.cx} y={c.cy + c.r * 0.55} state={renderedState} boxSize={boxSize} />
                )}
              </g>
            );
          }
          if (z.shape === "quad") {
            // Perspective-projected cell: the 4 corners are baked in. Label
            // stays axis-aligned at the projected centroid for readability.
            const q = zoneQuadPx(z, fit.imgW, fit.imgH);
            const pointsAttr = pointPairs(q.pts);
            // AABB diagonal-min — for trapezoid cells the vertical extent
            // is the meaningful "box size" since the label must fit within it.
            let minX = q.pts[0], maxX = q.pts[0], minY = q.pts[1], maxY = q.pts[1];
            for (let i = 2; i < q.pts.length; i += 2) {
              if (q.pts[i] < minX) minX = q.pts[i];
              if (q.pts[i] > maxX) maxX = q.pts[i];
              if (q.pts[i + 1] < minY) minY = q.pts[i + 1];
              if (q.pts[i + 1] > maxY) maxY = q.pts[i + 1];
            }
            const boxSize = Math.min(maxX - minX, maxY - minY);
            return (
              <g key={z.id} className={`${pulseClass} ${cursorClass}`} onClick={onClick}>
                <polygon
                  points={pointsAttr}
                  fill={bookedAgentTint ?? fill}
                  stroke={stroke}
                  strokeWidth={2}
                  strokeLinejoin="round"
                />
                <ZoneLabel x={q.cx} y={q.cy} label={z.label} boxSize={boxSize} />
                {props.showStateIcons && (
                  <StateIcon x={q.cx} y={q.cy + boxSize * 0.3} state={renderedState} boxSize={boxSize} />
                )}
              </g>
            );
          }
          const r = zoneRectPx(z, fit.imgW, fit.imgH);
          const boxSize = Math.min(r.w, r.h);
          // Inset for the corner state icon — sized to keep the icon fully
          // inside the rect at any zone size. Tied to the booked-tick radius
          // formula (max 10) + a 2 px gap so the icon never pokes outside.
          const iconInset = Math.max(4, Math.min(10, boxSize * 0.25)) + 2;
          return (
            <g
              key={z.id}
              className={`${pulseClass} ${cursorClass}`}
              onClick={onClick}
              transform={r.rotation ? `rotate(${r.rotation} ${r.x + r.w / 2} ${r.y + r.h / 2})` : undefined}
            >
              <rect
                x={r.x}
                y={r.y}
                width={r.w}
                height={r.h}
                fill={bookedAgentTint ?? fill}
                stroke={stroke}
                strokeWidth={2}
                rx={Math.min(r.w, r.h) * 0.08}
              />
              <ZoneLabel x={r.x + r.w / 2} y={r.y + r.h / 2} label={z.label} boxSize={boxSize} />
              {props.showStateIcons && (
                <StateIcon
                  x={r.x + r.w - iconInset}
                  y={r.y + iconInset}
                  state={renderedState}
                  boxSize={boxSize}
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// SVG `<polygon points="">` wants "x1,y1 x2,y2 …". We store flat pairs.
function pointPairs(pts: number[]): string {
  const out: string[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) out.push(`${pts[i]},${pts[i + 1]}`);
  return out.join(" ");
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function ZoneLabel({
  x, y, label, boxSize,
}: { x: number; y: number; label: string; boxSize: number }): JSX.Element {
  // fontSize scales with zone size — halfway between zone-fits and readable.
  // Floor 4 px so pinch-zoom can still lift it to readable; ceiling 24 px so
  // big-zone kiosks/TVs don't look comical.
  const fontSize = clamp(boxSize * 0.5, 4, 24);
  // Halo proportional to text — keeps the silhouette around digits crisp
  // without bleeding into neighbours when zones are tight.
  const strokeWidth = clamp(fontSize / 8, 0.5, 2.5);
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      className="fill-white font-display font-bold pointer-events-none"
      style={{
        fontSize,
        paintOrder: "stroke",
        // CSS var so light theme can flip the halo to white. Default
        // resolves to black with 40% alpha — same look as before.
        stroke: "rgb(var(--label-stroke) / 0.4)",
        strokeWidth,
      }}
    >
      {label}
    </text>
  );
}

function StateIcon({
  x, y, state, boxSize,
}: { x: number; y: number; state: string; boxSize: number }): JSX.Element | null {
  if (state === "booked") {
    // Hide the tick when the zone is too small for it to be legible — the
    // zone fill colour already encodes "booked" at any size.
    if (boxSize < 14) return null;
    const r = clamp(boxSize * 0.25, 4, 10);
    const stroke = clamp(r * 0.22, 0.8, 2);
    return (
      <g transform={`translate(${x - r} ${y - r})`} pointerEvents="none">
        <circle cx={r} cy={r} r={r} fill="rgb(var(--label-stroke) / 0.5)" />
        {/* Tick path — coords normalised to the 2r diameter box so it stays
            proportional at any radius. Originals (12 px box): M3 6.5 L5.5 9 L9.5 4. */}
        <path
          d={`M${r * 0.5} ${r * 1.08} L${r * 0.92} ${r * 1.5} L${r * 1.58} ${r * 0.67}`}
          stroke="white"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  }
  if (state === "pending" || state === "claiming") {
    if (boxSize < 10) return null;
    const r = clamp(boxSize * 0.15, 2.5, 6);
    return (
      <g transform={`translate(${x - r} ${y - r})`} pointerEvents="none">
        <circle
          cx={r}
          cy={r}
          r={r}
          fill="rgb(var(--z-pending))"
          stroke="white"
          strokeWidth={Math.max(0.6, r * 0.25)}
        />
      </g>
    );
  }
  return null;
}
